import { WeatherModelBase, ForecastInfo, SpotWeatherInfo, LocationAndTime, hydraForecastInfo } from './weather-model';
import { WeatherService } from '../services/weather-service';
import { VoyageRoutePointFeature } from './voyage-routing-model';
import { FetchStatusCodeError } from '../utils/auth-fetch';
import { deepEqual, isOnline } from '../utils/helpers';
import { captureLatestForecastVersionMetrics } from '../utils/rum-utility';

const ClimaticForecast = "F00000000000000";
const ClimaticAllowedElements = [ "Wind10M", "Wave", "WavePeriod", "Swell", "SwellPeriod", "AirTemperature", "Currents", "SeaWaterTemperature" ];

const ForecastTimeoutMinutes = 2;
const RecentForecastDays = 10; // Forecasts are valid for up to 9 days, so don't bother with older forecasts

const isVesselOnCloud = (<any>window)["appenv"].vesselOnCloud;

// Model for weather information
export class WeatherModelImpl extends WeatherModelBase
{
    elements: string[] = [ "Wind10M", "Wind50M", "WindGusts10M", "WindGusts50M", "RiskWindSpeed", "RiskWaveHeight",
                           "Wave", "WavePeriod", "Swell", "SwellPeriod", "Visibility", "Icing", "IceConcentration",
                           "FreezingLevel", "Currents", "Pressure", "AirTemperature", "Precipitation", "SeaWaterTemperature",
                           "WeatherCode", "HPa500", "RelativeHumidity", "FreezingLevel", "DewPoint", "SeaWaterSalinity"
                         ];

    constructor()
    {
        super();

        this._loadWeather();
        this._updateForecasts();
        // Check for new forecasts every ForecastTimeoutMinutes
       this._interval = window.setInterval( () => this._updateForecasts(), ForecastTimeoutMinutes*60000);
    }

    destroy()
    {
        window.clearInterval(this._interval);
    }

    get latestForecastVersion()
    {
        if (this._currentForecast)
        {
            return this._currentForecast.name;
        }
        return "";
    }

    get latestForecastInfo()
    {
        return this._currentForecast;
    }

    get climaticForecast()
    {
        return ClimaticForecast;
    }

    get recentForecastVersions()
    {
        return this._recentForecasts;
    }

    get hydraLatestForecastVersion() {
        if (this._hydraCurrentForecast) {
            return this._hydraCurrentForecast.name;
        }
        return "";
    }

    async getSpotWeather(coordinates: [number, number], timestamp: string)
    {
        timestamp = new Date(timestamp).toISOString();
        const key = this._getCacheKey(coordinates, timestamp);

        if (key in this._cachedSpotWeathers)
        {
            return this._cachedSpotWeathers[key];
        }

        if (!this.latestForecastVersion)
        {
            await this._updateForecasts();
        }

        let spotWeather = null;
        
        try
        {
            spotWeather = await WeatherService.getSpotWeather(coordinates, this.elements, timestamp, this._getForecastVersion(timestamp));
        }
        catch (e)
        {
            // Failure is likely due to weather subscription not covering the area - fall back to using climatic forecast
            const elements = this.elements.filter( element => ClimaticAllowedElements.includes(element) );
            spotWeather = await WeatherService.getSpotWeather(coordinates, elements, timestamp, ClimaticForecast);
        }

        if (spotWeather)
        {
            this._cachedSpotWeathers[key] = spotWeather;
            this._storeWeather();    
        }

        return spotWeather;
    }

    async getMultiSpotWeather(locationAndTimes: LocationAndTime[])
    {
        const fetchLocationTimes = [];
        const cachedWeathers = [];

        // Check if we have something cached already
        for (const locationTime of locationAndTimes)
        {
            const key = this._getCacheKey(locationTime.coordinates, locationTime.timestamp);
            if (key in this._cachedSpotWeathers)
            {
                cachedWeathers.push(this._cachedSpotWeathers[key]);
            }
            else
            {
                fetchLocationTimes.push(locationTime);
            }
        }

        let fetchedWeathers: SpotWeatherInfo[] = [];
        if (fetchLocationTimes.length > 0)
        {
            fetchedWeathers = await this._fetchMultiSpotWeather(fetchLocationTimes, this.elements);
            for (const spotWeather of fetchedWeathers)
            {
                const key = this._getCacheKey(spotWeather.geometry.coordinates, spotWeather.properties.timestamp!);
                this._cachedSpotWeathers[key] = spotWeather;
            }
            this._storeWeather();
        }

        // Merge cached and fetched spot weathers and sort based on timestamp
        let weathers = cachedWeathers.concat(fetchedWeathers);
        weathers.sort( (a, b) => {
            return (new Date(a.properties.timestamp!)).getTime() - (new Date(b.properties.timestamp!)).getTime();
        });

        return weathers;
    }

    private _recentForecasts: string[] = [];
    private _currentForecast: ForecastInfo|null = null;
    private _cachedSpotWeathers: { [index:string]: SpotWeatherInfo } = {};
    private _interval: number  = 0;
    private _hydraCurrentForecast: hydraForecastInfo|null = null;

    private async _updateForecasts()
    {
        if (isVesselOnCloud) {

            const isVessel = (<any>window)["appenv"].vesselIMONumber !== undefined;
            if (isVessel && !WeatherService.weatherForecastMonitorDisabled)
            {
                // When offline, use cached forecast from localStorage
                if (!isOnline()) {
                    if (this._currentForecast) {
                        this.dispatchEvent(new Event('weather-updated'));
                    }
                    return;
                }
                // Get current forecast from backend
                const latestAvailableForecast = await WeatherService.vesselGetLatestAvailableForecast();
                if (latestAvailableForecast) {
                    captureLatestForecastVersionMetrics(latestAvailableForecast);
                }
                if (latestAvailableForecast && !deepEqual(latestAvailableForecast, this._currentForecast))
                {
                    this._currentForecast = latestAvailableForecast;
                    // update the latest forecast in local storage
                    localStorage.setItem('currentForecast', JSON.stringify(latestAvailableForecast));
                    this._cachedSpotWeathers = {}; // Invalidate when forecast changes
                    this.dispatchEvent(new Event('weather-updated'));
                }
                // Get current forecast from hydra : only for cloud
                const hydraLatestAvailableForecast = await WeatherService.hydraLatestForecastForRouteClac();
                if(hydraLatestAvailableForecast && !deepEqual(hydraLatestAvailableForecast, this._hydraCurrentForecast)) {
                    this._hydraCurrentForecast = hydraLatestAvailableForecast;
                }
            }

        } else {
            const from = new Date(Date.now() - RecentForecastDays * 24*60*60*1000);
            let recentForecasts = await WeatherService.getForecasts(from);

            if (recentForecasts && recentForecasts.length)
            {
                this._recentForecasts = recentForecasts.map( f => f.name );

                const isVessel = (<any>window)["appenv"].vesselIMONumber !== undefined;
                if (isVessel && !WeatherService.weatherForecastMonitorDisabled)
                {
                    // Get current forecast from backend
                    const latestAvailableForecast = await WeatherService.vesselGetLatestAvailableForecast();
                    if (latestAvailableForecast) {
                        captureLatestForecastVersionMetrics(latestAvailableForecast);
                    }
                    if (latestAvailableForecast && !deepEqual(latestAvailableForecast, this._currentForecast))
                    {
                        this._currentForecast = latestAvailableForecast;
                        this._cachedSpotWeathers = {}; // Invalidate when forecast changes
                        this.dispatchEvent(new Event('weather-updated'));
                    }
                } else {
                    // Use recent forecasts to determine current forecast
                    const latestAvailableForecast: ForecastInfo = {
                        name: recentForecasts[recentForecasts.length-1].name,
                        timestamp: recentForecasts[recentForecasts.length-1].timestamp,
                        forecast_from: recentForecasts[recentForecasts.length-1].forecast_from,
                        forecast_to: recentForecasts[recentForecasts.length-1].forecast_to,
                        hasLowResData: true,
                        hasHighResData: true
                    }

                    if (recentForecasts[recentForecasts.length-1].subscriptions)
                    {
                        latestAvailableForecast.subscriptions = recentForecasts[recentForecasts.length-1].subscriptions;
                    }

                    if (!deepEqual(latestAvailableForecast, this._currentForecast))
                    {
                        this._currentForecast = latestAvailableForecast;
                        this._cachedSpotWeathers = {}; // Invalidate when forecast changes
                        this.dispatchEvent(new Event('weather-updated'));
                    }
                }
            }
            this._storeWeather();
            this._checkWeatherService();
        }    
    }

    private async _fetchMultiSpotWeather(locationAndTimes: LocationAndTime[], elements: string[])
    {
        let spots:VoyageRoutePointFeature[]  = [];
        try
        {
            spots = await WeatherService.getMultiSpotWeather(locationAndTimes, elements, this.latestForecastVersion);
        }
        catch (e)
        {
            if (e instanceof FetchStatusCodeError && e.response.status === 404)
            {
                // Failure is likely due to weather subscription not covering the area of one or more points - fall back to individual spot weathers
                // This can probably be removed once R11 is released everywhere we are running our frontend
                const getPromises: Promise<VoyageRoutePointFeature>[] = [];
                for (const location of locationAndTimes)
                {
                    getPromises.push(this.getSpotWeather(location.coordinates, location.timestamp));
                }
                spots = await Promise.all(getPromises);
            }
            else
            {
                throw e;
            }
        }

        // Sort result by timestamp
        spots.sort( (a, b) => new Date(b.properties.timestamp!).getTime() - new Date(a.properties.timestamp!).getTime() );

        return spots;
    }

    private _loadWeather()
    {
        try
        {
            const recentForecasts = JSON.parse(localStorage.getItem('recentForecasts') || "[]");
            if (recentForecasts.length > 0 && typeof recentForecasts[0] !== "string")
            {
                throw new Error("Old format");
            }
            this._recentForecasts = recentForecasts;
            this._currentForecast = JSON.parse(localStorage.getItem('currentForecast') || "null");
            this.setCacheLifecycle();
        }
        catch (error)
        {
            this._storeWeather();
        }
    }

    private _storeWeather()
    {
        localStorage.setItem('recentForecasts', JSON.stringify(this._recentForecasts));
        localStorage.setItem('currentForecast', JSON.stringify(this._currentForecast));
    }

    private _checkWeatherService() {
        const latestForecastTimestamp = new Date(this.latestForecastInfo?.timestamp || '');
        const dateNow = new Date();
        const lastUpdatedHours = Math.round(Math.abs(latestForecastTimestamp.getTime() - dateNow.getTime()) / (60 * 60 * 1000));

        // trigger event that shows weather-service-alert modal to user
        if (lastUpdatedHours >= 24) {
            this.dispatchEvent(new Event('show-weather-service-warning'));
        } else {
            // if update is new, remove existing lastWarningShownTimestamp from local storage set by weather-service-alert
            localStorage.removeItem('lastWarningShownTimestamp');
        }
    }

    private _getCacheKey(coordinates: [number, number], timestamp: string)
    {
        // Round timestamp to nearest second
        const time = Math.round(new Date(timestamp).getTime()/1000);
        // Using 4 digits after the decimal point gives a maximum position error of ~15 m
        return `${coordinates[0].toFixed(4)}${coordinates[1].toFixed(4)}${time}`;
    }

    private _getForecastVersion(timestamp: string)
    {
        const date = new Date(timestamp);
        const forecastInfo = this.latestForecastInfo!;

        if (date >= new Date(forecastInfo.forecast_from) && date <= new Date(forecastInfo.forecast_to))
        {
            return forecastInfo.name;
        }
        else
        {
            return ClimaticForecast;
        }
    }
}
